import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ApiClient, {
  KnownBadEmail,
  KnownBadEmailFilters,
  ObservationsFilters,
  ReputationObservation,
  ReputationStats,
} from '../api/client';
import { usePermissions } from '../context/PermissionContext';
import { DataTableShell, LocalTabs, MetricCard, PageHeader, SortableTableHeader } from './ui';

interface Props {
  apiClient: ApiClient;
}

type TabType = 'observations' | 'bad_emails';
type ObservationSortKey = 'created_at' | 'caller_app' | 'email' | 'ip' | 'asn' | 'recommendation' | 'score';
type EmailSortKey = 'email_normalized' | 'source' | 'first_seen_at' | 'last_seen_at' | 'observation_count';
type SortDirection = 'asc' | 'desc';

const RECOMMENDATION_COLORS: Record<string, { label: string; className: string }> = {
  allow: { label: 'Allow', className: 'status-approved' },
  shadowban: { label: 'Shadowban', className: 'status-flagged' },
  block: { label: 'Block', className: 'status-spam' },
};

const ReputationManager: React.FC<Props> = ({ apiClient }) => {
  const { hasPermission } = usePermissions();
  const canManage = hasPermission('reputation.manage');

  const [activeTab, setActiveTab] = useState<TabType>('observations');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [stats, setStats] = useState<ReputationStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);

  const [obs, setObs] = useState<ReputationObservation[]>([]);
  const [obsLoading, setObsLoading] = useState(true);
  const [obsPagination, setObsPagination] = useState({ page: 1, limit: 50, total: 0, totalPages: 0 });
  const [obsFilters, setObsFilters] = useState<ObservationsFilters>({});

  const [emails, setEmails] = useState<KnownBadEmail[]>([]);
  const [emailsLoading, setEmailsLoading] = useState(true);
  const [emailsPagination, setEmailsPagination] = useState({ page: 1, limit: 50, total: 0, totalPages: 0 });
  const [emailFilters, setEmailFilters] = useState<KnownBadEmailFilters>({});

  const [showAddForm, setShowAddForm] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [newEmailNotes, setNewEmailNotes] = useState('');
  const [adding, setAdding] = useState(false);

  const loadStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      setStats(await apiClient.getReputationStats());
    } catch (e: any) {
      setError(e?.message ?? 'Failed to load reputation stats');
    } finally {
      setStatsLoading(false);
    }
  }, [apiClient]);

  const loadObservations = useCallback(async () => {
    setObsLoading(true);
    try {
      const res = await apiClient.listReputationObservations({
        ...obsFilters,
        page: obsPagination.page,
        limit: obsPagination.limit,
      });
      setObs(res.data);
      setObsPagination(p => ({ ...p, total: res.meta.total, totalPages: res.meta.totalPages }));
    } catch (e: any) {
      setError(e?.message ?? 'Failed to load observations');
    } finally {
      setObsLoading(false);
    }
  }, [apiClient, obsFilters, obsPagination.limit, obsPagination.page]);

  const loadKnownBadEmails = useCallback(async () => {
    setEmailsLoading(true);
    try {
      const res = await apiClient.listKnownBadEmails({
        ...emailFilters,
        page: emailsPagination.page,
        limit: emailsPagination.limit,
      });
      setEmails(res.data);
      setEmailsPagination(p => ({ ...p, total: res.meta.total, totalPages: res.meta.totalPages }));
    } catch (e: any) {
      setError(e?.message ?? 'Failed to load known-bad emails');
    } finally {
      setEmailsLoading(false);
    }
  }, [apiClient, emailFilters, emailsPagination.limit, emailsPagination.page]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  useEffect(() => {
    if (activeTab === 'observations') {
      loadObservations();
    } else {
      loadKnownBadEmails();
    }
  }, [activeTab, loadKnownBadEmails, loadObservations]);

  const refresh = () => {
    setError(null);
    setSuccess(null);
    loadStats();
    if (activeTab === 'observations') {
      loadObservations();
    } else {
      loadKnownBadEmails();
    }
  };

  const handleAddEmail = async () => {
    if (!newEmail.trim()) return;
    setAdding(true);
    setError(null);
    try {
      await apiClient.addKnownBadEmail(newEmail.trim(), newEmailNotes.trim() || undefined);
      setSuccess(`Added ${newEmail}`);
      setNewEmail('');
      setNewEmailNotes('');
      setShowAddForm(false);
      loadKnownBadEmails();
      loadStats();
    } catch (e: any) {
      setError(e?.response?.data?.error ?? e?.message ?? 'Failed to add email');
    } finally {
      setAdding(false);
    }
  };

  const handleDeleteEmail = async (id: number, emailNormalized: string) => {
    if (!window.confirm(`Remove ${emailNormalized} from known-bad-emails?`)) return;
    setError(null);
    try {
      await apiClient.deleteKnownBadEmail(id);
      setSuccess(`Removed ${emailNormalized}`);
      loadKnownBadEmails();
      loadStats();
    } catch (e: any) {
      setError(e?.message ?? 'Failed to delete email');
    }
  };

  return (
    <div className="manager-page reputation-page">
      <PageHeader
        title="Reputation"
        actions={
          <>
            <button className="btn btn-secondary" onClick={refresh}>Refresh</button>
            {activeTab === 'bad_emails' && canManage && (
              <button className="btn btn-primary" onClick={() => setShowAddForm(v => !v)}>
                {showAddForm ? 'Cancel' : '+ Add Email'}
              </button>
            )}
          </>
        }
      />

      <LocalTabs
        tabs={[
          { id: 'observations', label: 'Observations' },
          { id: 'bad_emails', label: 'Known Bad Emails' },
        ]}
        active={activeTab}
        onChange={setActiveTab}
      />

      <div className="reputation-shell">
        {error && (
          <div className="alert alert-error">
            <span>Error</span>
            <span>{error}</span>
            <button className="alert-dismiss" onClick={() => setError(null)}>x</button>
          </div>
        )}

        {success && (
          <div className="alert alert-info">
            <span>Done</span>
            <span>{success}</span>
            <button className="alert-dismiss" onClick={() => setSuccess(null)}>x</button>
          </div>
        )}

        <div className="reputation-metrics">
          <MetricCard label="24h observations" value={statsLoading ? '...' : (stats?.observations_24h ?? 0).toLocaleString()} />
          <MetricCard label="7d observations" value={statsLoading ? '...' : (stats?.observations_7d ?? 0).toLocaleString()} />
          <MetricCard label="All-time observations" value={statsLoading ? '...' : (stats?.observations_all_time ?? 0).toLocaleString()} />
          <MetricCard label="Known bad emails" value={statsLoading ? '...' : (stats?.known_bad_emails_total ?? 0).toLocaleString()} tone="danger" />
        </div>

        {stats && stats.observations_24h > 0 && (
          <div className="reputation-distribution">
            <span>Last 24h</span>
            {Object.entries(stats.recommendation_distribution_24h).map(([rec, count]) => (
              <StatusBadge key={rec} value={rec} suffix={count.toLocaleString()} />
            ))}
          </div>
        )}

        {activeTab === 'observations' ? (
          <ObservationsTab
            observations={obs}
            loading={obsLoading}
            pagination={obsPagination}
            setPagination={setObsPagination}
            filters={obsFilters}
            setFilters={setObsFilters}
          />
        ) : (
          <BadEmailsTab
            emails={emails}
            loading={emailsLoading}
            pagination={emailsPagination}
            setPagination={setEmailsPagination}
            filters={emailFilters}
            setFilters={setEmailFilters}
            canManage={canManage}
            showAddForm={showAddForm}
            newEmail={newEmail}
            setNewEmail={setNewEmail}
            newEmailNotes={newEmailNotes}
            setNewEmailNotes={setNewEmailNotes}
            adding={adding}
            onAdd={handleAddEmail}
            onDelete={handleDeleteEmail}
          />
        )}
      </div>
    </div>
  );
};

const ObservationsTab: React.FC<{
  observations: ReputationObservation[];
  loading: boolean;
  pagination: { page: number; limit: number; total: number; totalPages: number };
  setPagination: React.Dispatch<React.SetStateAction<{ page: number; limit: number; total: number; totalPages: number }>>;
  filters: ObservationsFilters;
  setFilters: React.Dispatch<React.SetStateAction<ObservationsFilters>>;
}> = ({ observations, loading, pagination, setPagination, filters, setFilters }) => {
  const [sort, setSort] = useState<{ key: ObservationSortKey; direction: SortDirection }>({ key: 'created_at', direction: 'desc' });

  const sorted = useMemo(() => {
    return [...observations].sort((a, b) => compareValues(getObservationValue(a, sort.key), getObservationValue(b, sort.key), sort.direction));
  }, [observations, sort]);

  const updateSort = (key: ObservationSortKey) => {
    setSort(current => current.key === key
      ? { key, direction: current.direction === 'asc' ? 'desc' : 'asc' }
      : { key, direction: key === 'created_at' || key === 'score' ? 'desc' : 'asc' });
  };

  return (
    <div className="reputation-tab-panel">
      <section className="reputation-list-card">
        <div className="table-controls reputation-filter-row">
          <select
            value={filters.recommendation ?? ''}
            onChange={(e) => {
              setPagination(p => ({ ...p, page: 1 }));
              setFilters(f => ({ ...f, recommendation: (e.target.value || undefined) as any }));
            }}
          >
            <option value="">All recommendations</option>
            <option value="allow">Allow</option>
            <option value="shadowban">Shadowban</option>
            <option value="block">Block</option>
          </select>
          <input
            type="text"
            placeholder="Caller contains..."
            value={filters.caller_app ?? ''}
            onChange={(e) => {
              setPagination(p => ({ ...p, page: 1 }));
              setFilters(f => ({ ...f, caller_app: e.target.value || undefined }));
            }}
          />
          <input
            type="text"
            placeholder="Email exact"
            value={filters.email ?? ''}
            onChange={(e) => {
              setPagination(p => ({ ...p, page: 1 }));
              setFilters(f => ({ ...f, email: e.target.value || undefined }));
            }}
          />
          <input
            type="text"
            placeholder="IP exact"
            value={filters.ip ?? ''}
            onChange={(e) => {
              setPagination(p => ({ ...p, page: 1 }));
              setFilters(f => ({ ...f, ip: e.target.value || undefined }));
            }}
          />
        </div>

        {loading ? (
          <ReputationEmpty title="Loading observations" body="Checking the backend reputation log." />
        ) : observations.length === 0 ? (
          <ReputationEmpty title="No observations" body="Reputation scoring has not recorded any matching observations yet." />
        ) : (
          <DataTableShell className="reputation-table-wrap">
            <table className="data-table reputation-table">
              <thead>
                <tr>
                  <SortableTableHeader active={sort.key === 'created_at'} direction={sort.direction} onClick={() => updateSort('created_at')}>When</SortableTableHeader>
                  <SortableTableHeader active={sort.key === 'caller_app'} direction={sort.direction} onClick={() => updateSort('caller_app')}>Caller</SortableTableHeader>
                  <SortableTableHeader active={sort.key === 'email'} direction={sort.direction} onClick={() => updateSort('email')}>Email</SortableTableHeader>
                  <SortableTableHeader active={sort.key === 'ip'} direction={sort.direction} onClick={() => updateSort('ip')}>IP</SortableTableHeader>
                  <SortableTableHeader active={sort.key === 'asn'} direction={sort.direction} onClick={() => updateSort('asn')}>ASN</SortableTableHeader>
                  <SortableTableHeader active={sort.key === 'recommendation'} direction={sort.direction} onClick={() => updateSort('recommendation')}>Recommendation</SortableTableHeader>
                  <SortableTableHeader active={sort.key === 'score'} direction={sort.direction} onClick={() => updateSort('score')} align="right">Score</SortableTableHeader>
                  <th>Reasons</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map(o => (
                  <tr key={o.id}>
                    <td className="data-muted">{new Date(o.created_at).toLocaleString()}</td>
                    <td className="mono-cell">{o.caller_app}</td>
                    <td className="mono-cell">{o.email_normalized || o.email || '-'}</td>
                    <td className="mono-cell">{o.ip || '-'}</td>
                    <td className="mono-cell">{o.asn ?? '-'}</td>
                    <td><StatusBadge value={o.recommendation} /></td>
                    <td className="numeric-cell">{o.score.toFixed(2)}</td>
                    <td className="data-muted reasons-cell">{o.reasons.join(', ') || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </DataTableShell>
        )}
      </section>

      <Pagination pagination={pagination} setPagination={setPagination} />
    </div>
  );
};

const BadEmailsTab: React.FC<{
  emails: KnownBadEmail[];
  loading: boolean;
  pagination: { page: number; limit: number; total: number; totalPages: number };
  setPagination: React.Dispatch<React.SetStateAction<{ page: number; limit: number; total: number; totalPages: number }>>;
  filters: KnownBadEmailFilters;
  setFilters: React.Dispatch<React.SetStateAction<KnownBadEmailFilters>>;
  canManage: boolean;
  showAddForm: boolean;
  newEmail: string;
  setNewEmail: (v: string) => void;
  newEmailNotes: string;
  setNewEmailNotes: (v: string) => void;
  adding: boolean;
  onAdd: () => void;
  onDelete: (id: number, emailNormalized: string) => void;
}> = ({ emails, loading, pagination, setPagination, filters, setFilters, canManage, showAddForm, newEmail, setNewEmail, newEmailNotes, setNewEmailNotes, adding, onAdd, onDelete }) => {
  const [sort, setSort] = useState<{ key: EmailSortKey; direction: SortDirection }>({ key: 'last_seen_at', direction: 'desc' });

  const sorted = useMemo(() => {
    return [...emails].sort((a, b) => compareValues(getEmailValue(a, sort.key), getEmailValue(b, sort.key), sort.direction));
  }, [emails, sort]);

  const updateSort = (key: EmailSortKey) => {
    setSort(current => current.key === key
      ? { key, direction: current.direction === 'asc' ? 'desc' : 'asc' }
      : { key, direction: key.includes('_at') || key === 'observation_count' ? 'desc' : 'asc' });
  };

  return (
    <div className="reputation-tab-panel">
      {showAddForm && canManage && (
        <div className="inline-add-panel">
          <input type="email" placeholder="email@example.com" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} disabled={adding} />
          <input type="text" placeholder="Notes (optional)" value={newEmailNotes} onChange={(e) => setNewEmailNotes(e.target.value)} disabled={adding} />
          <button className="btn btn-primary" onClick={onAdd} disabled={adding || !newEmail.trim()}>
            {adding ? 'Adding...' : 'Add'}
          </button>
        </div>
      )}

      <section className="reputation-list-card">
        <div className="table-controls reputation-filter-row">
          <input
            type="text"
            placeholder="Search emails..."
            value={filters.q ?? ''}
            onChange={(e) => {
              setPagination(p => ({ ...p, page: 1 }));
              setFilters(f => ({ ...f, q: e.target.value || undefined }));
            }}
          />
          <select
            value={filters.source ?? ''}
            onChange={(e) => {
              setPagination(p => ({ ...p, page: 1 }));
              setFilters(f => ({ ...f, source: (e.target.value || undefined) as any }));
            }}
          >
            <option value="">All sources</option>
            <option value="manual">Manual</option>
            <option value="auto_promoted">Auto promoted</option>
          </select>
        </div>

        {loading ? (
          <ReputationEmpty title="Loading known-bad emails" body="Checking the normalized block list." />
        ) : emails.length === 0 ? (
          <ReputationEmpty title="No known-bad emails" body="Manually added or auto-promoted bad emails will appear here." />
        ) : (
          <DataTableShell className="reputation-table-wrap">
            <table className="data-table reputation-table">
              <thead>
                <tr>
                  <SortableTableHeader active={sort.key === 'email_normalized'} direction={sort.direction} onClick={() => updateSort('email_normalized')}>Email</SortableTableHeader>
                  <SortableTableHeader active={sort.key === 'source'} direction={sort.direction} onClick={() => updateSort('source')}>Source</SortableTableHeader>
                  <SortableTableHeader active={sort.key === 'first_seen_at'} direction={sort.direction} onClick={() => updateSort('first_seen_at')}>First seen</SortableTableHeader>
                  <SortableTableHeader active={sort.key === 'last_seen_at'} direction={sort.direction} onClick={() => updateSort('last_seen_at')}>Last seen</SortableTableHeader>
                  <SortableTableHeader active={sort.key === 'observation_count'} direction={sort.direction} onClick={() => updateSort('observation_count')} align="right">Hits</SortableTableHeader>
                  <th>Notes</th>
                  {canManage && <th className="actions-cell">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {sorted.map(e => (
                  <tr key={e.id}>
                    <td className="mono-cell">{e.email_normalized}</td>
                    <td><span className={`status-badge ${e.source === 'manual' ? 'status-approved' : 'status-flagged'}`}>{e.source === 'manual' ? 'Manual' : 'Auto promoted'}</span></td>
                    <td className="data-muted">{new Date(e.first_seen_at).toLocaleDateString()}</td>
                    <td className="data-muted">{new Date(e.last_seen_at).toLocaleDateString()}</td>
                    <td className="numeric-cell">{e.observation_count}</td>
                    <td className="data-muted reasons-cell">{e.notes ?? '-'}</td>
                    {canManage && (
                      <td className="actions-cell">
                        <button className="btn btn-sm btn-danger" onClick={() => onDelete(e.id, e.email_normalized)}>Remove</button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </DataTableShell>
        )}
      </section>

      <Pagination pagination={pagination} setPagination={setPagination} />
    </div>
  );
};

function StatusBadge({ value, suffix }: { value: string; suffix?: string }) {
  const config = RECOMMENDATION_COLORS[value] ?? { label: value, className: 'status-badge-neutral' };
  return (
    <span className={`status-badge ${config.className}`}>
      {config.label}{suffix ? ` ${suffix}` : ''}
    </span>
  );
}

function ReputationEmpty({ title, body }: { title: string; body: string }) {
  return (
    <div className="reputation-empty">
      <h3>{title}</h3>
      <p>{body}</p>
    </div>
  );
}

function Pagination({ pagination, setPagination }: {
  pagination: { page: number; limit: number; total: number; totalPages: number };
  setPagination: React.Dispatch<React.SetStateAction<{ page: number; limit: number; total: number; totalPages: number }>>;
}) {
  if (pagination.totalPages <= 1) return null;

  return (
    <div className="pagination-row">
      <button className="btn btn-secondary" disabled={pagination.page <= 1} onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}>
        Previous
      </button>
      <span>Page {pagination.page} of {pagination.totalPages} · {pagination.total.toLocaleString()} total</span>
      <button className="btn btn-secondary" disabled={pagination.page >= pagination.totalPages} onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}>
        Next
      </button>
    </div>
  );
}

function compareValues(a: string | number, b: string | number, direction: SortDirection) {
  const result = typeof a === 'number' && typeof b === 'number'
    ? a - b
    : String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
  return direction === 'asc' ? result : -result;
}

function getObservationValue(observation: ReputationObservation, key: ObservationSortKey): string | number {
  if (key === 'email') return observation.email_normalized || observation.email || '';
  if (key === 'ip') return observation.ip || '';
  if (key === 'asn') return observation.asn ?? -1;
  if (key === 'created_at') return new Date(observation.created_at).getTime();
  return observation[key] as string | number;
}

function getEmailValue(email: KnownBadEmail, key: EmailSortKey): string | number {
  if (key === 'first_seen_at' || key === 'last_seen_at') {
    return new Date(email[key]).getTime();
  }
  return email[key] as string | number;
}

export default ReputationManager;
